import { useUpdateOTLds } from '@phx-husky/use-update-ot-lds';
import { graphql } from 'gatsby';
import React, { FC, MouseEvent, useContext, useEffect } from 'react';

import { BodyRenderExternalData } from '~/components/BodyRender/models';
import ContactUsBlock from '~/components/ContactUsBlock';
import LdsBlock from '~/components/LdsBlock';
import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { Layout } from '~/layout/Layout';
import { contactUsBlockMapper } from '~/mappers/contactUsBlock';
import { ldsBlockMapper } from '~/mappers/ldsBlock';
import withContentstackPreview from '~/utils/livePreview';

import { ContentPageData } from './models';

export const LdsPage: FC<ContentPageData> = ({
  data: {
    profile,
    contentstackLdsPage,
    contentstackHeader,
    contentstackExtendedHeader,
    contentstackTranslations,
    contentstackHeaderWithSplitter,
    contentstackNewFooter,
    contentstackFooterWithSplitter,
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const t = mapContentstackTranslations(contentstackTranslations);

  const {
    seo,
    lds: [lds],
    pageHeader,
    fields,
    contactUs,
    openGraph,
  } = contentstackLdsPage;
  const { displayModal } = useContext(ExternalLinkDialogContext);

  const { settings, businessId, fields: ldsFields } = lds;

  const { updatedLdsContent, isCheckingLdsVersion } = useUpdateOTLds({
    baseUrl: settings[0].ot?.baseUrl,
    businessId,
    versionId: ldsFields.ldsVersionId,
    platform: settings[0].platform,
    locale,
  });

  useEffect(() => {
    const linksArray = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('.lds-block a')
    );

    linksArray.forEach((link) => {
      link.addEventListener('click', (e) =>
        displayModal(
          e as unknown as MouseEvent<HTMLAnchorElement>,
          link.getAttribute('href') as string
        )
      );
    });
  }, [displayModal]);

  const destructedContactUs =
    Array.isArray(contactUs) && contactUs.length > 0
      ? {
          ...contactUs[0],
          // To avoid duplication of entries with the same content in the CMS:
          pageBlock: { ...contactUs[0].pageBlock, mode: 'inverse' },
        }
      : undefined;

  const externalData: BodyRenderExternalData = { locale, jsonRTEImages };

  return (
    <Layout
      {...{
        openGraph,
        seo,
        headerBasic: {
          ...contentstackHeader,
        },
        extendedHeaderBasic: contentstackExtendedHeader,
        translations: t,
        headerWithSplitter: contentstackHeaderWithSplitter,
        headerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        footerBasic: contentstackNewFooter,
        footerWithSplitter: contentstackFooterWithSplitter,
        footerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        className: 'lds-page',
        pageUid: uid,
        locale,
        brand,
        pageHeaderProps: { pageHeader, fields },
        fullPath,
        profile,
      }}
    >
      {!isCheckingLdsVersion && (
        <LdsBlock {...ldsBlockMapper({ updatedLdsContent, ...lds })} />
      )}
      {destructedContactUs && (
        <ContactUsBlock {...contactUsBlockMapper(destructedContactUs)} />
      )}
    </Layout>
  );
};

export const query = graphql`
  query LdsPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackLdsPage(id: { eq: $id }) {
      uid
      updated_at
      publish_details {
        time
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      fields {
        ...PageFieldsFragment
      }
      contactUs: contact_us {
        ...ContactUsBlockFragment
      }
      lds {
        fields {
          ldsContent
          ldsVersionId
        }
        pageBlockUI: page_block_ui {
          ui {
            ...PageBlockUIFragment
          }
        }
        businessId: uri
        settings: lds_settings {
          ot {
            baseUrl: base_url
          }
          platform
        }
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackExtendedHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...ExtendedHeaderBasicFragment
    }
    contentstackTranslations(publish_details: { locale: { eq: $locale } }) {
      ...Translations
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackNewFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...NewFooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }
  }
`;

export default withContentstackPreview(LdsPage, 'lds_page');

export { Head } from '~/common/StructuredData';
